import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import { CustomerPondService } from '../../../core/services/customer-pond.service';
import CustomerPondModel from '../../../core/models/customer-pond.model';
import PondCultureModel from '../../../core/models/pond-culture.model';
import { ImageDetailsService } from 'src/app/core/services/images-details.service';
import ImageDetailsModel from 'src/app/core/models/images-details.model';
import { FarmSiteService } from '../../../core/services/farm-site.service';
import FarmSiteModel from 'src/app/core/models/farm-site.model';
import * as moment from 'moment';
import * as _ from 'lodash';
import { FormBuilder } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import RoutesModel from "../../../core/models/route.model";
import { BatchLoader } from "../../../core/utilities/batch-loader.util";

declare var $: any;

/**
 * @component BusinessEntityFarmSiteComponent
 * @selector app-business-entity-farm-site
 * @implements {OnInit, OnDestroy}
 *
 * @description
 * The `BusinessEntityFarmSiteComponent` displays details of a specific Farm Site, including a list of its ponds (Customer Ponds).
 * It allows users to view pond details, filter ponds, and perform actions like deleting pond data.
 *
 * ## Key Responsibilities:
 * 1. **Farm Site Details**: Fetches and displays details of the selected farm site.
 * 2. **Pond Listing**: Lists all ponds associated with the farm site.
 * 3. **Filtering**: Provides search functionality to filter the list of ponds.
 * 4. **Pagination**: Implements infinite scroll for the pond list using `BatchLoader`.
 * 5. **Data Management**: Allows deleting culture data or entire pond records.
 *
 * ## User Roles:
 * - **Admin/Business Admin**: Can view and manage farm sites and ponds.
 * - **Provider**: Can view farm sites associated with their business.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` retrieves route parameters (business ID, farm ID).
 * 2. **Data Fetching**: Fetches farm site details and then all associated ponds.
 * 3. **Display**: Ponds are loaded into `customerPondsList` via `BatchLoader`.
 *
 * @service FarmSiteService - For fetching farm site details.
 * @service CustomerPondService - For fetching pond details.
 * @service ImageDetailsService - For deleting pond data.
 * @service CommonMethodsService - For error handling.
 */
@Component({
  selector: 'app-customer-ponds',
  templateUrl: './customer-ponds.component.html',
  styleUrls: ['./customer-ponds.component.scss']
})
export class CustomerPondsComponent implements OnInit, OnDestroy {
  /** @type {FarmSiteModel} The farm site model. */
  public farmSite: FarmSiteModel;

  /** @type {Array<CustomerPondModel>} List of customer ponds. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {RoutesModel[]} List of customer ponds for display (paginated). */
  public customerPondsList: RoutesModel[] = [];

  /** @type {RoutesModel} Single customer pond model (unused). */
  public customerPond: RoutesModel = new RoutesModel();

  /** @type {RoutesModel[]} Filtered list of customer ponds. */
  public filteredCustomerPonds: RoutesModel[] = [];

  /** @type {Array<PondCultureModel>} List of pond cultures (unused). */
  public pondCultures: Array<PondCultureModel>;

  /** @type {Array<ImageDetailsModel>} List of image details. */
  public imageDetails: Array<ImageDetailsModel>;

  /** @type {ImageDetailsModel} Single image detail model (unused). */
  public imageDetail: ImageDetailsModel;

  /** @type {string} Business ID from route parameters. */
  public paramsbusinessId: string;

  /** @type {boolean} Page loading state flag. */
  public pgLoad = false;

  /** @type {string} Farm ID from route parameters. */
  public paramsfarmId: string;

  /** @type {boolean} Flag indicating if farm provider ID is present. */
  public navFarmProviderId: boolean;

  /** @type {string} Selected data type for deletion. */
  public selectedDeletedataType: string;

  /** @type {string} Selected pond ID for deletion. */
  public selectedPondId: string;

  /** @type {any} Business parameter (isAdmin). */
  public paramBusiness: any;

  /** @type {string} Search keyword. */
  public search: string;

  /** @type {boolean} Flag to clear search input. */
  public clearSearchInput: boolean;

  /** @type {boolean} Flag indicating no data message. */
  public NoDataMessage: boolean;

  /** @type {string} Logged-in user role. */
  public loginPersonType: string;

  /** @type {string} Logged-in business category. */
  public loginBusinessCategory: string;

  /** @type {boolean} List loader spinner flag. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} Start index for batch loading. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag indicating if filtering is active. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag indicating if list is empty. */
  public noList: boolean = false;

  /** @type {string} Duplicate farm provider ID. */
  public dubFarmProviderID: string;

  /** @type {boolean} Flag to show form/modal. */
  public showForm: boolean = false;

  /* End pagination, sorting, filter*/
  /** @type {any} Scroll timeout. */
  scrollTimeout: any;

  /**
   * Constructor for BusinessEntityFarmSiteComponent.
   *
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {ImageDetailsService} imageDetailService - Service for image details.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {CustomerPondService} customerPondService - Service for customer pond operations.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {ActivatedRoute} route - Active route service.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   * @param {Router} router - Angular Router.
   */
  constructor(private toasterService: ToastrService, public formBuilder: FormBuilder, public imageDetailService: ImageDetailsService, public farmSiteService: FarmSiteService, public customerPondService: CustomerPondService, public sidenavaction: Sidenavaction, public route: ActivatedRoute, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService, private router: Router
  ) {
  }

  /** @type {BatchLoader<any>} Batch loader utility instance. */
  private batchLoader: BatchLoader<any>;

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets previous route.
   * - Retrieves route parameters.
   * - Fetches farm site details and associated ponds.
   */
  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.customerPonds = [];
    this.customerPondsList = [];
    this.filteredCustomerPonds = [];
    this.isFiltering = false;
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.loginPersonType = localStorage.getItem('loginPersonType');
    this.loginBusinessCategory = localStorage.getItem('loginBusinessCategory');
    this.sidenavaction.x_navigation();
    this.route.params.subscribe(params => {
      this.paramsbusinessId = params.locId;
    });
    try {
      this.clearSearchInput = false;
      this.NoDataMessage = false;
      this.search = '';
      this.farmSite = await this.farmSiteService.getOneByID(this.paramsbusinessId);
      await this.getAllPondDetailsByFarmSiteId();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description
   * Lifecycle hook that is called when the component is destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests for FarmSiteService and CustomerPondService.
   */
  public ngOnDestroy(): void {
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getAllPondDetailsByFarmSiteId
   * @async
   * @description Fetches all ponds associated with the current farm site ID.
   */
  public async getAllPondDetailsByFarmSiteId() {
    try {
      this.customerPonds = await this.customerPondService.getAllPondDetailsByFarmSiteId(this.farmSite.id);
      this.pgLoad = true;
      for (let k = 0; k < this.customerPonds.length; k++) {
        const pondCulture = this.customerPonds[k].PondCultureData;
        for (let i = 0; i < pondCulture.length; i++) {
          const dateb = this.customerPonds[k].PondCultureData[i].start_date;
          if (dateb) {
            this.customerPonds[k].PondCultureData[i].start_date = moment(dateb).utc().format('DD-MMM-YYYY');
          }
        }
      }
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers batch loading.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Checks if scroll position is near bottom and loads next batch.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredCustomerPonds : this.customerPonds,
      (updatedList) => {
        this.customerPondsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method getDiffdaysfromtoday
   * @description Calculates the difference in days between the pond start date and today.
   * @param {PondCultureModel} pondculdata - The pond culture data.
   * @returns {number | string} The difference in days or empty string.
   */
  public getDiffdaysfromtoday(pondculdata: PondCultureModel) {
    if (pondculdata.start_date) {
      const a = pondculdata.start_date;
      const b = a.split('-');
      const startdate = '' + b[1] + ',' + b[0] + ',' + b[2];
      const dt1 = new Date(startdate);
      const dt2 = new Date();
      return Math.floor((Date.UTC(dt2.getFullYear(), dt2.getMonth(), dt2.getDate()) - Date.UTC(dt1.getFullYear(), dt1.getMonth(), dt1.getDate())) / (1000 * 60 * 60 * 24));
    }
    return '';
  }

  /**
   * @method isPondActive
   * @description Checks if a pond is currently active based on its culture data.
   * @param {CustomerPondModel} pond - The pond model.
   * @returns {boolean} True if active, false otherwise.
   */
  public isPondActive(pond: CustomerPondModel) {
    let isActive: boolean;
    if (pond.PondCultureData.length > 0) {
      if (pond.PondCultureData[pond.PondCultureData.length - 1].end_date) {
        isActive = false;
      } else {
        isActive = true;
      }
    } else {
      isActive = false;
    }
    return isActive;
  }

  /**
   * @method getPondRelatedData
   * @async
   * @description Fetches related data (images) for a specific pond to prepare for deletion.
   * @param {string} pondId - The pond ID.
   */
  public async getPondRelatedData(pondId: string) {
    this.showForm = true;
    try {
      this.selectedPondId = _.clone(pondId);
      this.imageDetails = await this.imageDetailService.getPonds([pondId]);
      const keys = Object.keys(this.imageDetails[0]);
      const values = Object.values(this.imageDetails[0]);
      let text = '';
      for (let i = 0; i < keys.length; i++) {
        text += keys[i] + '-' + values[i] + '\n';
      }
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method deletePond
   * @async
   * @description Deletes pond data based on selected type (Data only or Data + Pond).
   */
  public async deletePond() {
    try {
      if (!this.selectedDeletedataType) {
        this.toasterService.error('Please select "Delete Culture Data" or "Delete Culture Data and Pond"');
        return false;
      }

      // No need to flip the value manually
      let pond_delete = this.selectedDeletedataType;

      this.imageDetails = await this.imageDetailService.deletePond([this.selectedPondId], pond_delete);

      this.toasterService.success(
        `${this.selectedDeletedataType === 'True' ? 'Data and Pond' : 'Data'} deleted successfully`,
        '',
        { timeOut: 2000 }
      );

      this.showForm = false;
      this.selectedDeletedataType = '';
      this.getAllPondDetailsByFarmSiteId();
    } catch (error) {
      alert(error);
    }
  }

  /**
   * @method clear
   * @description Clears the deletion selection and hides the form.
   */
  public clear() {
    this.selectedDeletedataType = undefined;
    this.showForm = false;
  }

  /**
   * @method navigateBackWithProvider
   * @description Navigates back to the business entity farms list (with provider context).
   */
  navigateBack(): void {
    this.router.navigate([
      `/home/customer-locations`,
      this.farmSite.businessID,
    ]);
  }
}
